import MaterialTable from "material-table";
import * as Defaults from "Globals/Defaults/TableOptions";
import React, { useImperativeHandle, useEffect, useState } from "react";
import { DarwinTablePageContent } from "Globals/Styles/AppStyling";
import { ProjectDirectoryModel } from "./ProjectDirectoryModel";
import { ClickableImgCell } from "Components/Table/ClickableImgCell";
import { useObserver } from "mobx-react-lite";
import { DeleteModal } from "Components/Modal/DeleteModal";
import { EditDirectoryEntryModal } from "./EditDirectoryEntryModal";

import EmailLogo from "Content/Email.svg";
import EditLogo from "Content/AddEdit.svg";
import DeleteLogo from "Content/Bin.svg";

interface IProjectDirectoryListProps {
    entries: ProjectDirectoryModel[];
    onUpdate: (entry: ProjectDirectoryModel) => Promise<void>;
    onDelete: (entryId: string) => Promise<void>;
    isLoading: boolean;
}

export const ProjectDirectoryList = React.forwardRef((props: IProjectDirectoryListProps, ref: any) => {
    const { entries, onUpdate, onDelete, isLoading } = props;
    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [editOpen, setEditOpen] = React.useState(false);
    const [selectedEntry, setSelectedEntry] = React.useState<ProjectDirectoryModel | undefined>(undefined);
    const [errorMessage, setErrorMessage] = React.useState("");

    useEffect(() => {
        return () => {
            // Cleanup if needed
        };
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            cleanUp() {
                // Cleanup if needed
            },
        }),
        [],
    );

    const editClick = (entry: ProjectDirectoryModel) => {
        if (!props.isLoading) {
            setSelectedEntry(entry);
            setEditOpen(true);
        }
    };

    const deleteClick = (entry: ProjectDirectoryModel) => {
        if (!props.isLoading) {
            setSelectedEntry(entry);
            setDeleteOpen(true);
        }
    };

    const formatEmail = (email: string | undefined): any => {
        if (!email) return null;
        const mailto: string = "mailto:" + email;
        return (
            <a href={mailto} className="selectable-cell">
                <img src={EmailLogo} alt="Email contact" />
            </a>
        );
    };

    const formatEdit = (entry: ProjectDirectoryModel): any => {
        return <ClickableImgCell logo={EditLogo} text={"Edit entry"} onClick={() => editClick(entry)} id={entry.id || ""} className="selectable-cell" showText={false} />;
    };

    const formatDelete = (entry: ProjectDirectoryModel): any => {
        return (
            <ClickableImgCell logo={DeleteLogo} text={"Delete entry"} onClick={() => deleteClick(entry)} id={entry.id || ""} className="delete selectable-cell" showText={false} />
        );
    };

    const handleDeleteClose = () => {
        setDeleteOpen(false);
        setSelectedEntry(undefined);
    };

    const handleEditClose = () => {
        setEditOpen(false);
        setSelectedEntry(undefined);
        setErrorMessage("");
    };

    const handleDeleteConfirm = async () => {
        if (selectedEntry?.id) {
            await onDelete(selectedEntry.id);
        }
        handleDeleteClose();
    };

    const handleEditSave = async (updatedEntry: ProjectDirectoryModel) => {
        await onUpdate(updatedEntry);
        handleEditClose();
    };

    let columns: any[] = [
        { title: "First name", field: "firstName" },
        { title: "Last name", field: "lastName" },
        { title: "Company", field: "company" },
        { title: "Role", field: "role" },
        { title: "Email", field: "emailAddress" },
        { title: "Phone", field: "phoneNumber" },
        {
            title: "",
            field: "id",
            sorting: false,
            filtering: false,
            width: 15,
            render: (rowData: ProjectDirectoryModel) => formatEmail(rowData.emailAddress),
        },
        {
            title: "",
            field: "id",
            sorting: false,
            filtering: false,
            width: 15,
            render: (rowData: ProjectDirectoryModel) => formatEdit(rowData),
        },
        {
            title: "",
            field: "id",
            sorting: false,
            filtering: false,
            width: 15,
            render: (rowData: ProjectDirectoryModel) => formatDelete(rowData),
        },
    ];

    return useObserver(() => (
        <DarwinTablePageContent>
            <DeleteModal
                open={deleteOpen}
                onClose={handleDeleteClose}
                onDelete={handleDeleteConfirm}
                title="Delete Directory Entry?"
                text="Are you sure you want to delete this directory entry?"
            />
            {selectedEntry && <EditDirectoryEntryModal entry={selectedEntry} open={editOpen} onClose={handleEditClose} onSave={handleEditSave} isLoading={props.isLoading} />}
            <MaterialTable columns={columns} options={Defaults.GetDarwinTableOptionsNoSearch()} data={entries} title="" />
        </DarwinTablePageContent>
    ));
});
